import React, {
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { animated, useSpring } from 'react-spring';
import { Field, Form, Formik } from 'formik';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import PropTypes from 'prop-types';

import { MessageContainer } from './ForgotPasswordForm';
import Button from '../../../../shared/_TBComponents/Button';
import Checkmark from '../../../../shared/_TBComponents/Checkmark';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';
import { passwordRequirements, PasswordSchema } from '../../../../shared/utils/registrationSchemaHelper';
import useReset from '../hooks/useReset';

const FormGroup = styled('div')`
  z-index: 1;

  .error {
    border: 2px solid #CC0404;
  }
  .text-center {
    margin-top: 25px;
    text-align: center;
  }
`;

const PasswordFieldWrapper = styled('div')`
  position: relative;
`;

const PasswordToggleButton = styled('button')`
  align-items: center;
  background-color: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  justify-content: center;
  padding: 8px;
  pointer-events: auto;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);

  &:focus {
    outline: none;
  }

  &:hover {
    color: #333;
  }
`;

const FieldError = styled('div')`
  color: #CC0404;
  font-size: 13px;
  font-weight: 400;
  margin: 4px 0px;
`;

const ResetPasswordForm = ({ isIdnty, isSetPassword, tokenString }) => {
  const isComplete = useSelector((state) => state.forgot.ui.isComplete);
  const isDisabled = useSelector((state) => state.forgot.ui.isDisabled);
  const isSubmitting = useSelector((state) => state.forgot.ui.isSubmitting);
  const resetError = useSelector((state) => state.forgot.ui.resetError);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { handleReset } = useReset();

  const completeAnimation = useSpring({ opacity: isComplete ? 1 : 0 });
  const formAnimation = useSpring({
    opacity: isSubmitting || isComplete ? 0 : 1,
    zIndex: 1,
  });
  const spinnerAnimation = useSpring({ opacity: isSubmitting ? 1 : 0, zIndex: 0 });

  const theme = useTheme();

  return (
    <Formik
      initialValues={{
        grant_type: 'password',
        password: '',
        confirm: '',
        token: tokenString,
        client_id: process.env.CLIENT_ID,
        client_secret: process.env.CLIENT_SECRET,
        scope: 'offline_access',
      }}
      onSubmit={(values, { setSubmitting }) => {
        handleReset(
          { token: tokenString, password: values.password },
          isIdnty,
          isIdnty ? process.env.IDNTY_EASY_JOIN_ID : undefined,
        );
        setSubmitting(false);
      }}
      validationSchema={PasswordSchema}
      render={({
        errors,
        touched,
        handleSubmit,
      }) => (
        <>
          <animated.div style={formAnimation}>
            <Form
              name={isSetPassword ? 'set-password' : 'reset-password'}
              id={isSetPassword ? 'set-password' : 'reset-password'}
            >
              <FormGroup className='form-group'>
                <Text
                  styledTextProps={{ fontSize: '16px', fontWeight: '700' }}
                >
                  New Password
                </Text>
                <PasswordFieldWrapper>
                  <Field
                    autoComplete='off'
                    className={`form-control ${errors.password && touched.password ? 'error' : ''}`}
                    id='new-password-field'
                    type={showPassword ? 'text' : 'password'}
                    name='password'
                    placeholder='Password'
                    disabled={isDisabled}
                  />
                  <PasswordToggleButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    tabIndex={-1}
                    type='button'
                  >
                    {showPassword ? 'Hide Password' : 'Show Password'}
                    <span style={{ marginLeft: '8px' }}>
                      {showPassword ? <HiEyeOff /> : <HiEye />}
                    </span>
                  </PasswordToggleButton>
                </PasswordFieldWrapper>
                {errors.password && touched.password
                  ? <FieldError>{errors.password}</FieldError>
                  : (
                    <Text
                      styledTextProps={{ fontSize: '13px', fontWeight: '400' }}
                      styledWrapperProps={{ margin: '4px 0px' }}
                    >
                      {passwordRequirements}
                    </Text>
                  )}
              </FormGroup>
              <FormGroup className='form-group'>
                <Text
                  styledTextProps={{ fontSize: '16px', fontWeight: '700' }}
                  styledWrapperProps={{ marginTop: '20px' }}
                >
                  Confirm Password
                </Text>
                <PasswordFieldWrapper>
                  <Field
                    autoComplete='off'
                    className={`form-control ${errors.confirmPassword && touched.confirmPassword ? 'error' : ''}`}
                    id='confirm-password-field'
                    type={showConfirmPassword ? 'text' : 'password'}
                    name='confirmPassword'
                    placeholder='Password'
                    disabled={isDisabled}
                  />
                  <PasswordToggleButton
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    tabIndex={-1}
                    type='button'
                  >
                    {showConfirmPassword ? 'Hide Password' : 'Show Password'}
                    <span style={{ marginLeft: '8px' }}>
                      {showConfirmPassword ? <HiEyeOff /> : <HiEye />}
                    </span>
                  </PasswordToggleButton>
                </PasswordFieldWrapper>
                {errors.confirmPassword && touched.confirmPassword
                  && <FieldError>{errors.confirmPassword}</FieldError>}
                {resetError
                  && <FieldError className='text-center'>Something went wrong, please try again.</FieldError>}
              </FormGroup>
              <FormGroup className='text-center'>
                <Button
                  bgColor={isIdnty ? '#00F5F5' : theme.colors.orange}
                  disabled={isDisabled || errors.password || errors.confirmPassword}
                  fontSize='14px'
                  fullWidth
                  isLoading={isSubmitting}
                  large
                  lightSpinner
                  name='submit'
                  noBorder
                  onClick={handleSubmit}
                  rounded
                  styledWrapperProps={{
                    marginTop: '40px',
                  }}
                  txtColor={isIdnty ? '#000000' : theme.colors.white}
                  upperCase
                />
              </FormGroup>
            </Form>
          </animated.div>
          <animated.div style={spinnerAnimation}>
            <MessageContainer>
              <Spinner />
            </MessageContainer>
          </animated.div>
          <animated.div style={completeAnimation}>
            <MessageContainer>
              <Checkmark />
            </MessageContainer>
          </animated.div>
        </>
      )}
    />
  );
};

ResetPasswordForm.propTypes = {
  isIdnty: PropTypes.bool,
  isSetPassword: PropTypes.bool,
  tokenString: PropTypes.string.isRequired,
};

ResetPasswordForm.defaultProps = {
  isIdnty: false,
  isSetPassword: false,
};

export default ResetPasswordForm;
